import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import GD from '../GD'

const ChatUploadingFilesDiv = styled.div`
	font-size: 20px;
	position: absolute;
	top: 66px;
	right: 8px;
	display: flex;
	min-width: 200px;
	flex-direction: column;
	align-items: flex-end;
	z-index: 30;
`

const ChatUploadingFileDiv = styled.div`
  max-width: 200px;
	width: 100%;
	padding: 18px;
	font-size: 14px;
	background-color: #3b548e;
	cursor: pointer;
	transition: 0.3s ease;
	opacity: 0.7;
	&:hover{
		opacity: 1;
		transition: 0.3s ease;
	}
	color: white;
	border-radius: 10px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin-bottom: 2px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
	position: relative;
	display: flex;
  flex-direction: column;
  gap: 5px;
`

const ProgressBar = styled.div<{ progress: number }>`
	position: absolute;
	bottom: 0;
	left: 0;
	width: ${({ progress }) => `${progress ?? 0}%`};
	height: 4px;
	background-color: #4caf50;
	transition: 0.5s ease;
`

const ChatInProgressUploadingFile = styled.div`
	width: 100%;
`
const ChatUploadingFilesCntText = styled.div`
	font-size: 14px;
	color: rgba(255, 255, 255, 0.7);
`
const ChatUploadingFilesTitleText = styled.div`
	font-size: 12px;
	color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`
const SuccessUpload = styled(ChatUploadingFilesDiv)`
		background-color: rgb(48,162,94);
    opacity: 1;
    border-radius: 8px;
    font-size: 14px;
    padding: 10px 0;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-weight: 200;
    color: #ffffff;
    top: 24px;
`
const ErrorText = styled(SuccessUpload)`
    background-color: rgb(162,48,48);
`

const useUploadFiles = () => {
  const [files, setFiles] = useState<UploadingFileVO[]>([])
  const [uploadingFileInfo, setUploadingFile] = useState<UploadingFileVO | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [success, setSuccess] = useState<boolean | null>(null)
  const [error, setError] = useState<boolean | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const errorShownUids = useRef<string[]>([])

  const handleFileListUpdate = useCallback((uploadingFiles: UploadingFileVO[]) => {
    const allFilesCompleted = uploadingFiles.every(({ status }) => status !== 'start')
    const newErrorUids: Record<string, boolean> = {}
    const inProgressUploads = uploadingFiles.filter(({ status }) => status === 'start')

    uploadingFiles.forEach(({ status, tempUID }) => {
      if (status === 'fail' && !errorShownUids.current[tempUID] && allFilesCompleted) {
        newErrorUids[tempUID] = true
        errorShownUids.current[tempUID] = true
      }
    })

    const hasError = Object.keys(newErrorUids).length > 0

    if (isUploading && allFilesCompleted) {
      setSuccess(!hasError)
      setError(hasError)

      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setSuccess(null)
        setError(null)
        setFiles([])
      }, 3500)
    }

    setIsUploading(!!inProgressUploads.length)
    setFiles(uploadingFiles)
  }, [isUploading])

  const handleFileProgressUpdate = useCallback((packet: { progress: number, tempUID: string }) => {
    if (success !== null) setSuccess(null)
    setProgress(packet.progress)

    if (uploadingFileInfo?.tempUID === packet.tempUID) return

    const currentFile = files.find((file) => file.tempUID === packet.tempUID)

    if (!currentFile) return

    setUploadingFile(currentFile)
  }, [files, success, uploadingFileInfo])

  useEffect(() => {
    GD.FILE_UPLOADING_LIST_UPDATED.subscribe(handleFileListUpdate, 'useUploadFiles')
    GD.FILE_PROGRESS_UPDATE.subscribe(handleFileProgressUpdate, 'useUploadFiles')

    return () => {
      GD.FILE_UPLOADING_LIST_UPDATED.unsubscribe('useUploadFiles')
      GD.FILE_PROGRESS_UPDATE.unsubscribe('useUploadFiles')
    }
  }, [handleFileListUpdate, handleFileProgressUpdate])

  return { files, uploadingFileInfo, isUploading, progress, success, error }
}

const UploadIndicator = () => {
  const {
    files,
    uploadingFileInfo,
    isUploading,
    progress,
    success,
    error,
  } = useUploadFiles()

  const inProcessFiles = files.filter((file) => file.status === 'start')
  if (!isUploading && !success && !error && !inProcessFiles.length) return null

  return (
    <ChatUploadingFilesDiv>
      {success ? (
        <SuccessUpload>All files have been sent</SuccessUpload>
      ) : error ? (
        <ErrorText>Oops! There was an error on some files. Click for more info.</ErrorText>
      ) : (
        <ChatInProgressUploadingFile>
          <ChatUploadingFileDiv>
            <ChatUploadingFilesCntText>Uploading, {inProcessFiles.length} items left</ChatUploadingFilesCntText>
            <ChatUploadingFilesTitleText>{uploadingFileInfo?.file?.name ?? ''}</ChatUploadingFilesTitleText>
            <ProgressBar progress={progress} />
          </ChatUploadingFileDiv>
        </ChatInProgressUploadingFile>
      )}
    </ChatUploadingFilesDiv>
  )
}

export default UploadIndicator
